import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook to detect if the current user has a desktop tier subscription
 * @returns Object with isDesktopTier boolean and loading/error states
 */
export function useDesktopTier() {
  const { token } = useAuth();
  const [isDesktopTier, setIsDesktopTier] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkDesktopTier = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!token) {
        setIsDesktopTier(false);
        setLoading(false);
        return;
      }

      const response = await apiService.getSubscription(token);
      if (response.success && response.data) {
        const tier = response.data.tier || 'trial';
        // Only the desktop tier gets the limited-mode UI. Admins return a synthetic
        // 'admin' tier from the backend and must keep full access (not limited mode).
        setIsDesktopTier(tier === 'desktop');
      } else {
        setIsDesktopTier(false);
      }
    } catch (err) {
      console.error('Error checking desktop tier:', err);
      setError(err instanceof Error ? err.message : 'Failed to check subscription tier');
      setIsDesktopTier(false);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkDesktopTier();
  }, [checkDesktopTier]);

  return {
    isDesktopTier,
    loading,
    error,
    refetch: checkDesktopTier
  };
}

